import Plyr from 'plyr';
import React, { ReactNode } from 'react';
import './VideoPlayer.scss';

export interface ICaptionsConfig {
  lang: string;
  src: string;
  label: string;
}

interface IVideoPlayerProps {
  language?: string;
  videoSrc: string;
  captions?: Array<ICaptionsConfig>;
  onCloseHandler?: () => void;
  onVideoStartedHandler?: () => void;
  onVideoCompletedHandler?: () => void;
}

const ControlsWithoutCaptions = [
  'play-large',
  'play',
  'progress',
  'current-time',
  'mute',
  'volume',
  'settings',
  'airplay',
  'fullscreen',
];

const ControlsWithCaptions = [...ControlsWithoutCaptions, 'captions'];

function getPlayerConfig(language?: string, withCaptions?: boolean) {
  if (!withCaptions) {
    return {
      controls: ControlsWithoutCaptions,
    };
  }
  return {
    controls: ControlsWithCaptions,
    captions: { active: true, language: language ?? 'auto' },
    settings: ['captions'],
  };
}

const VideoPlayer: React.FC<IVideoPlayerProps> = ({
  language,
  videoSrc,
  captions,
  onCloseHandler,
  onVideoCompletedHandler,
  onVideoStartedHandler,
}) => {
  const playerRef = React.useRef<Plyr | undefined>();
  React.useEffect(() => {
    if (!playerRef.current) {
      // if player not running yet - set it up
      const player = new Plyr('#player', getPlayerConfig(language, !!captions));
      player.pip = false;
      player.on('playing', () => {
        onVideoStartedHandler && onVideoStartedHandler();
      });
      player.on('ended', () => {
        onVideoCompletedHandler && onVideoCompletedHandler();
      });
      playerRef.current = player;
    } else {
      // if player is running - update language
      playerRef.current.language = language ?? 'de';
    }
  }, [onVideoStartedHandler, onVideoCompletedHandler, captions, language, playerRef]);
  const canClose = !!onCloseHandler;
  let captionsTracks: Array<ReactNode> | null = null;
  if (captions) {
    captionsTracks = [];
    for (const caption of captions) {
      captionsTracks.push(
        <track
          key={caption.lang}
          kind="captions"
          label={caption.label}
          src={caption.src}
          srcLang={caption.lang}
          data-testid={`captions-track-${caption.lang}`}
        />
      );
    }
  }
  return (
    <div className="video-wrapper">
      <video
        disablePictureInPicture
        autoPlay
        crossOrigin="anonymous"
        playsInline
        src={videoSrc}
        id="player"
        controlsList="nodownload"
        data-testid="video-player">
        {captionsTracks}
      </video>
      {canClose ? (
        <span className="plyr__control close-button" data-testid="player-close-button" onClick={onCloseHandler}>
          <svg
            role="img"
            className="close-button-icon"
            width="100%"
            height="100%"
            viewBox="0 0 50 50"
            xmlns="http://www.w3.org/2000/svg">
            <path fill="currentColor" id="svg_3" d="m0,0l50,50" strokeWidth="6" stroke="currentColor" />
            <path fill="currentColor" id="svg_4" d="m50,0l-50,50" strokeWidth="6" stroke="currentColor" />
          </svg>
        </span>
      ) : null}
    </div>
  );
};
export default VideoPlayer;
